"use client";

import { cn } from "@/lib/utils";
import { useLegendItem } from "./legend-context";

export type LegendMarkerVariant =
  | "dot"
  | "line"
  | "dashed"
  | "arrow"
  | "diamond"
  | "chevron"
  | "square"
  | "swatch"
  | "number";

export interface LegendMarkerProps {
  /** Marker size class. Default: "h-2.5 w-2.5" for dot, "h-5 w-5" for number. */
  className?: string;
  /** Marker shape. Default "dot". */
  variant?: LegendMarkerVariant;
}

export function LegendMarker({
  className,
  variant = "dot",
}: LegendMarkerProps) {
  const { item, index } = useLegendItem();

  if (variant === "number") {
    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full border font-mono font-medium tabular-nums",
          "border-[color:var(--legend-muted-foreground)]",
          "h-5 w-5 text-[10px]",
          className,
        )}
      >
        {index + 1}
      </div>
    );
  }

  if (variant === "line") {
    return (
      <span
        className={cn("inline-block h-[2px] w-4 shrink-0 rounded-full", className)}
        style={{ background: item.color }}
      />
    );
  }

  if (variant === "dashed") {
    return (
      <svg width="18" height="6" className={cn("shrink-0", className)}>
        <line
          x1="1"
          y1="3"
          x2="17"
          y2="3"
          stroke={item.color}
          strokeWidth="1.5"
          strokeDasharray="3 3"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (variant === "arrow") {
    return (
      <svg
        width="20"
        height="8"
        className={cn("shrink-0", className)}
        style={{ overflow: "visible" }}
      >
        <line
          x1="1"
          y1="4"
          x2="14"
          y2="4"
          stroke={item.color}
          strokeWidth="1.3"
          strokeDasharray={item.dashed ? "3 3" : "0"}
          strokeLinecap="round"
        />
        <polygon points="14,1 19,4 14,7" fill={item.color} />
      </svg>
    );
  }

  if (variant === "diamond") {
    return (
      <svg width="12" height="12" className={cn("shrink-0", className)}>
        <rect
          x="2"
          y="2"
          width="8"
          height="8"
          transform="rotate(45 6 6)"
          fill="none"
          stroke={item.color}
          strokeWidth="1.3"
        />
      </svg>
    );
  }

  if (variant === "square") {
    return (
      <span
        className={cn("inline-block h-2.5 w-2.5 shrink-0 rounded-sm", className)}
        style={{ background: item.color }}
      />
    );
  }

  if (variant === "swatch") {
    const fill = item.swatchVariant ?? "outline";
    return (
      <span
        className={cn(
          "inline-block h-3.5 w-5 shrink-0 rounded-[4px] border",
          fill === "dashed" && "border-dashed",
          className,
        )}
        style={{
          borderColor: item.color,
          backgroundColor:
            fill === "ghost"
              ? `color-mix(in srgb, ${item.color} 35%, transparent)`
              : undefined,
        }}
      />
    );
  }

  if (variant === "chevron") {
    return (
      <svg width="14" height="10" className={cn("shrink-0", className)}>
        <polygon
          points="1,1 9,1 13,5 9,9 1,9 3,5"
          fill={item.color}
          fillOpacity="0.25"
          stroke={item.color}
          strokeWidth="1"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <div
      className={cn("shrink-0 rounded-full", className ?? "h-2.5 w-2.5")}
      style={{ backgroundColor: item.color }}
    />
  );
}

LegendMarker.displayName = "LegendMarker";
